<template>
  <div class="smap" :class="{ drawing }">
    <div ref="map" class="smap-canvas"></div>

    <!-- search: an address, a place or "lat, lng" -->
    <div class="smap-search" v-if="!readonly">
      <i class="fas fa-search"></i>
      <input v-model="query" type="search" placeholder="Search an address, a place or lat, lng" autocomplete="off"
             @input="onQuery" @keydown.enter.prevent="pickFirst" @keydown.esc="results = []">
      <button v-if="canLocate" type="button" class="smap-icon" title="Use my current location" @click="locateMe">
        <i class="fas" :class="locating ? 'fa-circle-notch fa-spin' : 'fa-location-crosshairs'"></i>
      </button>
      <ul v-if="results.length" class="smap-results">
        <li v-for="r in results" :key="r.id" @mousedown.prevent="pick(r)">
          <i class="fas fa-map-marker-alt"></i>
          <span><b>{{ r.title }}</b><small>{{ r.subtitle }}</small></span>
        </li>
      </ul>
    </div>

    <div class="smap-tools" v-if="!readonly">
      <button type="button" :class="{ on: style === 'satellite' }" @click="style = 'satellite'"><i class="fas fa-satellite"></i><span>Satellite</span></button>
      <button type="button" :class="{ on: style === 'map' }" @click="style = 'map'"><i class="fas fa-map"></i><span>Map</span></button>
      <button type="button" class="smap-draw" :class="{ on: drawing }" @click="drawing ? cancelDraw() : startDraw()">
        <i class="fas fa-draw-polygon"></i><span>{{ drawing ? 'Cancel drawing' : (drawn ? 'Redraw boundary' : 'Draw boundary') }}</span>
      </button>
    </div>

    <div class="smap-hint" v-if="drawing">
      <span><i class="fas fa-mouse-pointer"></i> Click the corners of the forest · {{ vertices.length }} points{{ vertices.length > 2 ? ` · ${fmtHa(liveArea)} ha` : '' }}</span>
      <button type="button" @click="undoVertex" :disabled="!vertices.length"><i class="fas fa-undo"></i></button>
      <button type="button" class="ok" @click="finishDraw" :disabled="vertices.length < 3"><i class="fas fa-check"></i> Done</button>
    </div>
    <div class="smap-hint smap-hint--idle" v-else-if="!readonly && !marker">
      <span><i class="fas fa-hand-pointer"></i> Search the place or click the map: we look for the forest there</span>
    </div>

    <div class="smap-attrib" v-if="attributions.length">{{ attributions.join(' · ') }}</div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import area from '@turf/area';
import { MAPBOX_TOKEN } from '../../utils/mapbox';

const STYLES = { satellite: 'mapbox://styles/mapbox/satellite-streets-v12', map: 'mapbox://styles/mapbox/outdoors-v12' };
const SOURCE_COLORS = { 'fi-cadastre': '#5fb4f0', 'fi-stands': '#f4c25b', 'es-cadastre': '#5fb4f0', osm: '#c9f7b3', drawn: '#ffffff' };
const COORDS = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,; ]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

export default {
  name: 'SimMap',
  props: {
    features: { type: Array, default: () => [] }, // detected features, each with properties.selected
    drawn: { type: Object, default: null }, // GeoJSON Polygon geometry
    marker: { type: Object, default: null }, // { lat, lng }
    attributions: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      map: null, style: 'satellite', query: '', results: [], searchTimer: null, locating: false,
      drawing: false, vertices: [], mapMarker: null, resizeObserver: null, loaded: false, rendered: false, awaitingRender: false,
    };
  },
  computed: {
    canLocate() { return typeof navigator !== 'undefined' && !!navigator.geolocation; },
    liveArea() {
      if (this.vertices.length < 3) return 0;
      return area({ type: 'Polygon', coordinates: [[...this.vertices, this.vertices[0]]] }) / 10000;
    },
    featureData() {
      return {
        type: 'FeatureCollection',
        features: this.features.filter((f) => f.geometry).map((f) => ({
          type: 'Feature',
          geometry: f.geometry,
          properties: {
            id: f.properties.id,
            selected: f.properties.selected ? 1 : 0,
            color: SOURCE_COLORS[f.properties.source] || '#ffffff',
            label: f.properties.mapLabel || '',
          },
        })),
      };
    },
    drawData() {
      const features = [];
      if (this.drawing) {
        if (this.vertices.length > 2) features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...this.vertices, this.vertices[0]]] }, properties: { kind: 'area' } });
        if (this.vertices.length > 1) features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: this.vertices }, properties: { kind: 'line' } });
        this.vertices.forEach((v, i) => features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: v }, properties: { kind: i === 0 ? 'first' : 'vertex' } }));
      } else if (this.drawn) {
        features.push({ type: 'Feature', geometry: this.drawn, properties: { kind: 'area' } });
      }
      return { type: 'FeatureCollection', features };
    },
  },
  watch: {
    featureData() { this.setData('sim-features', this.featureData); },
    drawData() { this.setData('sim-draw', this.drawData); },
    marker: { handler() { this.placeMarker(); }, deep: true },
    style(s) {
      if (!this.map) return;
      // the marker is put back once the new style is ready (mapbox-gl reads the style's fog for markers)
      this.dropMarker();
      this.loaded = false;
      this.rendered = false;
      this.map.setStyle(STYLES[s]);
    },
  },
  mounted() {
    mapboxgl.accessToken = MAPBOX_TOKEN;
    this.map = new mapboxgl.Map({
      container: this.$refs.map,
      style: STYLES[this.style],
      center: this.marker ? [this.marker.lng, this.marker.lat] : [20, 55],
      zoom: this.marker ? 14 : 3.2,
      attributionControl: false,
    });
    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    this.map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');
    this.map.on('style.load', this.onStyle);
    this.map.on('click', this.onClick);
    this.map.on('mousemove', 'sim-features-fill', () => { if (!this.drawing) this.map.getCanvas().style.cursor = 'pointer'; });
    this.map.on('mouseleave', 'sim-features-fill', () => { if (!this.drawing) this.map.getCanvas().style.cursor = ''; });
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.map && this.map.resize());
      this.resizeObserver.observe(this.$el);
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    // a marker still on the map while it is removed makes mapbox-gl read a fog that no longer exists
    this.dropMarker();
    if (this.map) this.map.remove();
  },
  methods: {
    fmtHa(v) { return new Intl.NumberFormat('en-GB', { maximumFractionDigits: v < 10 ? 2 : 1 }).format(v); },
    setData(id, data) {
      const src = this.map && this.loaded && this.map.getSource(id);
      if (src) src.setData(data);
    },
    onStyle() {
      const map = this.map;
      this.loaded = true;
      map.addSource('sim-features', { type: 'geojson', data: this.featureData });
      map.addSource('sim-draw', { type: 'geojson', data: this.drawData });
      map.addLayer({
        id: 'sim-features-fill', type: 'fill', source: 'sim-features',
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': ['case', ['==', ['get', 'selected'], 1], 0.34, 0.07] },
      });
      map.addLayer({
        id: 'sim-features-line', type: 'line', source: 'sim-features',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['case', ['==', ['get', 'selected'], 1], 2.6, 1.1],
          'line-opacity': ['case', ['==', ['get', 'selected'], 1], 1, 0.75],
        },
      });
      map.addLayer({
        id: 'sim-features-label', type: 'symbol', source: 'sim-features', minzoom: 14,
        layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'], 'text-max-width': 8 },
        paint: { 'text-color': '#ffffff', 'text-halo-color': 'rgba(0,0,0,0.75)', 'text-halo-width': 1.4 },
      });
      map.addLayer({ id: 'sim-draw-fill', type: 'fill', source: 'sim-draw', filter: ['==', ['get', 'kind'], 'area'], paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.22 } });
      map.addLayer({ id: 'sim-draw-outline', type: 'line', source: 'sim-draw', filter: ['in', ['get', 'kind'], ['literal', ['area', 'line']]], paint: { 'line-color': '#ffffff', 'line-width': 2.4, 'line-dasharray': [2, 1.2] } });
      map.addLayer({
        id: 'sim-draw-vertex', type: 'circle', source: 'sim-draw', filter: ['in', ['get', 'kind'], ['literal', ['vertex', 'first']]],
        paint: { 'circle-radius': ['case', ['==', ['get', 'kind'], 'first'], 7, 5], 'circle-color': ['case', ['==', ['get', 'kind'], 'first'], '#9ed77f', '#ffffff'], 'circle-stroke-color': '#1b3a22', 'circle-stroke-width': 2 },
      });
      this.placeMarker();
    },
    onClick(e) {
      if (this.readonly) return;
      const lngLat = [e.lngLat.lng, e.lngLat.lat];
      if (this.drawing) {
        // clicking the first point closes the shape
        if (this.vertices.length > 2) {
          const first = this.map.project(this.vertices[0]);
          if (Math.hypot(first.x - e.point.x, first.y - e.point.y) < 12) { this.finishDraw(); return; }
        }
        this.vertices.push(lngLat);
        return;
      }
      const hit = this.map.queryRenderedFeatures(e.point, { layers: ['sim-features-fill'] });
      if (hit.length) {
        // the smallest polygon under the cursor is the one meant
        const ids = hit.map((h) => h.properties.id);
        const candidates = this.features.filter((f) => ids.includes(f.properties.id)).sort((a, b) => (a.properties.areaHa || 0) - (b.properties.areaHa || 0));
        if (candidates.length) { this.$emit('toggle', candidates[0].properties.id); return; }
      }
      this.$emit('locate', { lat: e.lngLat.lat, lng: e.lngLat.lng, label: null, fromClick: true });
    },
    dropMarker() {
      if (this.mapMarker) { this.mapMarker.remove(); this.mapMarker = null; }
    },
    placeMarker() {
      // mapbox-gl places markers using the style's fog, whose values only exist once a frame has been drawn:
      // a marker added between style.load and that first frame throws
      if (!this.map || !this.loaded) return;
      if (!this.rendered) {
        if (!this.awaitingRender) {
          this.awaitingRender = true;
          this.map.once('render', () => {
            this.awaitingRender = false;
            this.rendered = true;
            this.placeMarker();
          });
        }
        return;
      }
      if (!this.marker) {
        if (this.mapMarker) { this.mapMarker.remove(); this.mapMarker = null; }
        return;
      }
      if (!this.mapMarker) {
        const el = document.createElement('div');
        el.className = 'smap-pin';
        el.innerHTML = '<span></span>';
        this.mapMarker = new mapboxgl.Marker({ element: el, anchor: 'bottom' });
      }
      this.mapMarker.setLngLat([this.marker.lng, this.marker.lat]).addTo(this.map);
    },
    flyTo(lat, lng, zoom = 15) {
      if (this.map) this.map.flyTo({ center: [lng, lat], zoom, speed: 1.6, essential: true });
    },
    /** Frames the given geometries (detected or drawn). */
    fitTo(geometries) {
      if (!this.map || !geometries.length) return;
      const bounds = new mapboxgl.LngLatBounds();
      const walk = (c) => (typeof c[0] === 'number' ? bounds.extend(c) : c.forEach(walk));
      geometries.forEach((g) => g && walk(g.coordinates));
      if (!bounds.isEmpty()) this.map.fitBounds(bounds, { padding: 60, maxZoom: 16.5, duration: 1200 });
    },
    // ---- search (Mapbox geocoding with the frontend's public token)
    onQuery() {
      clearTimeout(this.searchTimer);
      const q = this.query.trim();
      if (q.length < 3) { this.results = []; return; }
      const m = q.match(COORDS);
      if (m) {
        this.results = [{ id: 'coords', title: `${Number(m[1]).toFixed(5)}, ${Number(m[2]).toFixed(5)}`, subtitle: 'Coordinates', lat: Number(m[1]), lng: Number(m[2]) }];
        return;
      }
      this.searchTimer = setTimeout(() => this.search(q), 280);
    },
    async search(q) {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&limit=6&language=en`;
        const json = await (await fetch(url)).json();
        if (q !== this.query.trim()) return;
        this.results = (json.features || []).map((f) => ({
          id: f.id,
          title: f.text,
          subtitle: f.place_name,
          lat: f.center[1],
          lng: f.center[0],
          country: ((f.context || []).find((c) => String(c.id).startsWith('country')) || {}).short_code || (f.properties && f.properties.short_code) || null,
          bbox: f.bbox || null,
        }));
      } catch (e) {
        this.results = [];
      }
    },
    pickFirst() { if (this.results.length) this.pick(this.results[0]); },
    pick(r) {
      this.results = [];
      this.query = r.subtitle && r.id !== 'coords' ? r.subtitle : r.title;
      this.flyTo(r.lat, r.lng, 15);
      this.$emit('locate', { lat: r.lat, lng: r.lng, label: r.id === 'coords' ? null : r.subtitle, country: r.country ? String(r.country).slice(0, 2).toUpperCase() : null });
    },
    locateMe() {
      this.locating = true;
      navigator.geolocation.getCurrentPosition((pos) => {
        this.locating = false;
        this.flyTo(pos.coords.latitude, pos.coords.longitude, 15.5);
        this.$emit('locate', { lat: pos.coords.latitude, lng: pos.coords.longitude, label: 'My current location' });
      }, () => {
        this.locating = false;
        this.$emit('error', 'The browser could not get your location (permission denied or not available).');
      }, { enableHighAccuracy: true, timeout: 12000 });
    },
    // ---- drawing
    startDraw() {
      this.drawing = true;
      this.vertices = [];
      if (this.map) { this.map.doubleClickZoom.disable(); this.map.getCanvas().style.cursor = 'crosshair'; }
    },
    stopDraw() {
      this.drawing = false;
      this.vertices = [];
      if (this.map) { this.map.doubleClickZoom.enable(); this.map.getCanvas().style.cursor = ''; }
    },
    cancelDraw() { this.stopDraw(); },
    undoVertex() { this.vertices.pop(); },
    finishDraw() {
      if (this.vertices.length < 3) return;
      const geometry = { type: 'Polygon', coordinates: [[...this.vertices, this.vertices[0]].map(([x, y]) => [Math.round(x * 1e6) / 1e6, Math.round(y * 1e6) / 1e6])] };
      this.stopDraw();
      this.$emit('drawn', geometry);
    },
  },
};
</script>

<style lang="scss">
/* the pin lives outside the component's DOM (mapbox appends it), so it is not scoped */
.smap-pin {
  width: 30px;
  height: 40px;
  display: flex;
  justify-content: center;

  span {
    width: 26px;
    height: 26px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    background: linear-gradient(135deg, #9ed77f, #2f8f5b);
    border: 3px solid #fff;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.45);
    animation: smap-drop 0.5s cubic-bezier(.2, 1.4, .4, 1) both;
  }
}

@keyframes smap-drop {
  from { transform: translateY(-30px) rotate(-45deg); opacity: 0; }
  to { transform: translateY(0) rotate(-45deg); opacity: 1; }
}
</style>

<style scoped lang="scss">
.smap {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 300px;
  border-radius: 18px;
  overflow: hidden;
  background: #0d1a12;
}

.smap-canvas { position: absolute; inset: 0; }

.smap-search {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  max-width: 460px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 6px 0 14px;
  height: 44px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.45);
  z-index: 3;

  > i { color: #4f8c4a; }

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font-size: 0.92rem;
    color: #1d2a20;
  }
}

.smap-icon {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: #eef6ee;
  color: #33633a;
  &:hover { background: #dcefdc; }
}

.smap-results {
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  margin: 0;
  padding: 6px;
  list-style: none;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 16px 40px -12px rgba(0, 0, 0, 0.45);
  max-height: 280px;
  overflow: auto;

  li {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 9px 10px;
    border-radius: 10px;
    cursor: pointer;
    color: #1d2a20;
    &:hover { background: #f0f7f0; }
    i { color: #4f8c4a; margin-top: 3px; }
    span { display: flex; flex-direction: column; min-width: 0; }
    b { font-weight: 700; font-size: 0.88rem; }
    small { color: #647368; font-size: 0.75rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  }
}

.smap-tools {
  position: absolute;
  top: 64px;
  left: 12px;
  display: flex;
  gap: 6px;
  z-index: 2;
  flex-wrap: wrap;

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 11px;
    border: 0;
    border-radius: 10px;
    font-size: 0.78rem;
    font-weight: 700;
    color: #fff;
    background: rgba(12, 24, 16, 0.62);
    backdrop-filter: blur(6px);
    transition: background 0.2s;

    &.on { background: rgba(79, 140, 74, 0.95); }
    &:hover { background: rgba(79, 140, 74, 0.8); }
  }

  .smap-draw.on { background: #e06a44; }
}

.smap-hint {
  position: absolute;
  left: 50%;
  bottom: 14px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px 6px 14px;
  border-radius: 999px;
  background: rgba(12, 24, 16, 0.78);
  color: #fff;
  font-size: 0.8rem;
  white-space: nowrap;
  z-index: 2;
  backdrop-filter: blur(6px);
  animation: smap-in 0.4s ease both;

  button {
    border: 0;
    height: 28px;
    min-width: 28px;
    padding: 0 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    font-weight: 700;
    &.ok { background: #4f8c4a; }
    &:disabled { opacity: 0.4; }
  }
}

.smap-hint--idle { padding: 8px 16px; }

@keyframes smap-in {
  from { opacity: 0; transform: translate(-50%, 8px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}

.smap-attrib {
  position: absolute;
  right: 46px;
  bottom: 6px;
  max-width: 55%;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.6rem;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.88);
  background: rgba(0, 0, 0, 0.45);
  text-align: right;
  z-index: 1;
  pointer-events: none;
}

@media (max-width: 575px) {
  .smap-tools button span { display: none; }
  .smap-hint { font-size: 0.72rem; max-width: calc(100% - 20px); white-space: normal; }
  .smap-attrib { display: none; }
}
</style>
